const LOW = [60, 500], FLAT = [80, 2000], HISS = [2000, 6000];
const FRAME_MS = 30, CALIBRATE_MS = 1000, SUSTAIN_MS = 120, RELEASE_MS = 160;
export const FLAT_MIN = 0.35;
export const COVER_MIN = 0.32;

export const breathState = {
  listening: false, phase: "idle", ctx: "none", rate: 0, settings: null,
  low: -120, hiss: -120, flat: 0, floor: null, hissFloor: null, thr: null,
  pre: 0, level: 0, active: false, gusts: 0, silent: false,
};

function band(freq, hz, [a, b]) {
  const lo = Math.max(1, Math.round(a / hz)), hi = Math.min(freq.length, Math.round(b / hz));
  let sum = 0, logSum = 0;
  for (let i = lo; i < hi; i++) {
    const p = Math.pow(10, freq[i] / 10) + 1e-14;
    sum += p;
    logSum += Math.log(p);
  }
  const n = Math.max(1, hi - lo);
  return { db: 10 * Math.log10(sum / n), flat: Math.exp(logSum / n) / (sum / n) };
}

function whiteFlat(freq, hz, [a, b], w = 6) {
  const lo = Math.max(1 + w, Math.round(a / hz)), hi = Math.min(freq.length - w, Math.round(b / hz));
  const p = (i) => Math.pow(10, freq[i] / 10) + 1e-14;
  let sum = 0, logSum = 0;
  for (let i = lo; i < hi; i++) {
    let loc = 0;
    for (let j = i - w; j <= i + w; j++) loc += p(j);
    const r = p(i) / (loc / (2 * w + 1));
    sum += r;
    logSum += Math.log(r);
  }
  const n = Math.max(1, hi - lo);
  return Math.exp(logSum / n) / (sum / n);
}

const median = (a) => [...a].sort((x, y) => x - y)[Math.floor(a.length / 2)];
const spread = (a, m) => Math.sqrt(a.reduce((s, v) => s + (v - m) ** 2, 0) / a.length);

export function detector() {
  const cal = { low: [], hiss: [], bins: null, frames: 0 };
  let t = 0, onMs = 0, offMs = 0;
  return (freq, hz, dt = FRAME_MS) => {
    const s = breathState;
    const low = band(freq, hz, LOW), hiss = band(freq, hz, HISS);
    const b0 = Math.max(1, Math.round(LOW[0] / hz)), b1 = Math.round(LOW[1] / hz);
    s.low = +low.db.toFixed(1);
    s.hiss = +hiss.db.toFixed(1);
    s.flat = +whiteFlat(freq, hz, FLAT).toFixed(3);
    s.silent = !Number.isFinite(low.db) || low.db < -125;
    t += dt;
    if (t < CALIBRATE_MS) {
      s.phase = "calibrating";
      if (Number.isFinite(low.db)) {
        cal.low.push(low.db);
        cal.hiss.push(hiss.db);
        cal.bins = cal.bins || new Float32Array(b1 - b0);
        for (let i = b0; i < b1; i++) cal.bins[i - b0] += Math.max(-140, freq[i]);
        cal.frames++;
      }
      return s;
    }
    if (s.floor === null) {
      s.floor = cal.low.length ? median(cal.low) : -90;
      s.hissFloor = cal.hiss.length ? median(cal.hiss) : -100;
      s.margin = Math.max(4.5, Math.min(12, 2.5 * Math.max(1.5, spread(cal.low, s.floor))));
      cal.bins = cal.bins ? cal.bins.map((v) => v / cal.frames) : new Float32Array(b1 - b0).fill(-100);
      s.phase = "ready";
    }
    let up = 0;
    for (let i = b0; i < b1; i++) if (freq[i] > cal.bins[i - b0] + 7) up++;
    s.cover = +(up / (b1 - b0)).toFixed(2);
    s.thr = +(s.floor + s.margin).toFixed(1);
    const hissUp = hiss.db - s.hissFloor > 6;
    const cand = s.flat > FLAT_MIN && s.cover >= COVER_MIN && (low.db > s.thr || (low.db > s.thr - 3 && hissUp));
    onMs = cand ? onMs + dt : 0;
    offMs = cand ? 0 : offMs + dt;
    if (!s.active && onMs >= SUSTAIN_MS) s.active = true;
    if (s.active && offMs >= RELEASE_MS) s.active = false;
    if (!cand && low.db < s.thr) s.floor += (low.db - s.floor) * 0.004;
    s.pre = +Math.max(0, Math.min(1, (low.db - s.floor) / (s.margin * 1.6))).toFixed(2);
    const target = s.active ? Math.min(1, 0.3 + (low.db - s.thr) / 18) : 0;
    s.level = +(s.level + (target - s.level) * 0.35).toFixed(3);
    return s;
  };
}

export function startBreath(onFrame) {
  // a context of its own, created and resumed inside the tap: a shared music context that predates the mic
  // can be interrupted on iOS or keep a playback route on Android, and then the analyser only ever hears silence
  const Ctx = window.AudioContext || window.webkitAudioContext;
  let ctx = new Ctx();
  ctx.resume?.().catch(() => {});
  Object.assign(breathState, { phase: "asking", floor: null, thr: null, level: 0, pre: 0, active: false });
  if (!navigator.mediaDevices?.getUserMedia) return Promise.reject(new Error("no-mic"));
  return navigator.mediaDevices
    .getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 }, video: false })
    .then(async (stream) => {
      const track = stream.getAudioTracks()[0];
      await track?.applyConstraints?.({ echoCancellation: false, noiseSuppression: false, autoGainControl: false }).catch(() => {});
      let src;
      try {
        src = ctx.createMediaStreamSource(stream);
      } catch {
        ctx.close?.().catch(() => {});
        ctx = new Ctx();
        src = ctx.createMediaStreamSource(stream);
      }
      if (ctx.state !== "running") await ctx.resume().catch(() => {});
      let an = ctx.createAnalyser();
      an.fftSize = 2048;
      an.smoothingTimeConstant = 0.15;
      src.connect(an);
      const freq = new Float32Array(an.frequencyBinCount);
      let hz = ctx.sampleRate / an.fftSize;
      let detect = detector();
      const s = track?.getSettings?.() || {};
      Object.assign(breathState, {
        listening: true, phase: "calibrating", rate: ctx.sampleRate, ctx: ctx.state,
        settings: { ec: s.echoCancellation, ns: s.noiseSuppression, agc: s.autoGainControl },
        resume: () => ctx.state !== "running" && ctx.resume().catch(() => {}),
      });
      let was = ctx.state;
      let silentMs = 0, rebuilt = false;
      // iOS: a context suspended by a call, the lock screen or a background tab comes back when the page is visible or touched
      const wake = () => document.visibilityState === "visible" && ctx.state !== "running" && ctx.resume().catch(() => {});
      document.addEventListener("visibilitychange", wake);
      const id = setInterval(() => {
        breathState.ctx = ctx.state;
        // a context that only starts running later (resumed by the next tap) calibrates afresh
        if (ctx.state === "running" && was !== "running") {
          breathState.floor = null;
          detect = detector();
        }
        was = ctx.state;
        an.getFloatFrequencyData(freq);
        const st = detect(freq, hz);
        // a running context that hears pure digital silence from a live mic (an iOS sample-rate mismatch) is rebuilt once at the mic's own rate
        silentMs = ctx.state === "running" && st.silent && track?.readyState === "live" ? silentMs + FRAME_MS : 0;
        if (silentMs > 1500 && !rebuilt) {
          rebuilt = true;
          try {
            const rate = track.getSettings?.().sampleRate;
            const fresh = rate ? new Ctx({ sampleRate: rate }) : new Ctx();
            const s2 = fresh.createMediaStreamSource(stream);
            const a2 = fresh.createAnalyser();
            a2.fftSize = 2048;
            a2.smoothingTimeConstant = 0.15;
            s2.connect(a2);
            src.disconnect();
            ctx.close().catch(() => {});
            ctx = fresh;
            src = s2;
            an = a2;
            hz = ctx.sampleRate / an.fftSize;
            breathState.rate = ctx.sampleRate;
            breathState.resume = () => ctx.state !== "running" && ctx.resume().catch(() => {});
            ctx.resume().catch(() => {});
            breathState.floor = null;
            detect = detector();
          } catch {
            /* keep the first context */
          }
        }
        onFrame(st);
      }, FRAME_MS);
      return () => {
        clearInterval(id);
        document.removeEventListener("visibilitychange", wake);
        stream.getTracks().forEach((tr) => tr.stop());
        src.disconnect();
        ctx.close().catch(() => {});
        Object.assign(breathState, { listening: false, phase: "idle", level: 0, pre: 0, active: false, resume: null });
      };
    })
    .catch((e) => {
      ctx.close?.().catch(() => {});
      throw e;
    });
}
